import { ScanSearch, UserCheck, Wrench, CheckCircle2 } from 'lucide-react';

interface CaseData {
  status: string;
  severity: string;
  assigned_user_name: string | null;
  entity_host: string | null;
  title: string;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  last_reported_at: string | null;
}

interface Note {
  id: number;
  note: string;
  user_name: string | null;
  created_at: string;
}

interface Report {
  id: number;
  sent_to: string;
  sent_at: string;
}

const STEPS = [
  { key: 'detect', label: 'Detect Threats', icon: ScanSearch },
  { key: 'review', label: 'Review by Expert', icon: UserCheck },
  { key: 'respond', label: 'Respond', icon: Wrench },
  { key: 'fix', label: 'Fix Incident', icon: CheckCircle2 },
];

function duration(from: string, to: string | null): string {
  if (!to) return '—';
  const minutes = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}min`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// Avanzamento del servizio MDR ricostruito dagli eventi già tracciati sull'incident:
// rilevamento (apertura), presa in carico (assegnazione o prima nota), risposta al cliente
// (segnalazione inviata) e chiusura.
export default function MdrServiceStatus({ caseData, notes, reports }: { caseData: CaseData; notes: Note[]; reports: Report[] }) {
  const firstNote = notes[0] || null;
  const firstReport = [...reports].sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime())[0] || null;

  const reviewedAt = firstNote?.created_at || (caseData.assigned_user_name ? caseData.updated_at : null);
  const respondedAt = firstReport?.sent_at || null;
  const fixedAt = caseData.closed_at;

  const done: Record<string, string | null> = {
    detect: caseData.created_at,
    review: reviewedAt,
    respond: respondedAt,
    fix: fixedAt,
  };
  const currentIndex = STEPS.findIndex(s => !done[s.key]);

  const log = [
    { label: 'Detect Threats', at: caseData.created_at, text: `Threats detected (${caseData.title}) on the host (${caseData.entity_host || 'unknown'})` },
    reviewedAt && { label: 'Review by Expert', at: reviewedAt, text: firstNote ? `Reviewed by ${firstNote.user_name || 'analyst'}: ${firstNote.note}` : `Assigned to ${caseData.assigned_user_name}` },
    respondedAt && { label: 'Respond', at: respondedAt, text: `Report sent to the customer (${firstReport!.sent_to})` },
    fixedAt && { label: 'Fix Incident', at: fixedAt, text: 'Incident closed' },
  ].filter(Boolean) as { label: string; at: string; text: string }[];

  return (
    <div className="p-5">
      <div className="flex items-start justify-center gap-2 mb-6">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          const isDone = !!done[step.key];
          const isCurrent = i === currentIndex;
          return (
            <div key={step.key} className="flex items-start gap-2">
              <div className="flex flex-col items-center w-32">
                <Icon size={20} className={isDone ? 'text-primary-600' : isCurrent ? 'text-amber-500' : 'text-gray-300'} />
                <span className={`text-xs mt-1 text-center ${isDone ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                  {step.label}
                </span>
                <span className="text-[11px] text-gray-400">
                  {done[step.key] ? new Date(done[step.key]!).toLocaleString('en-GB') : isCurrent ? 'in progress' : 'pending'}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <span className={`mt-2.5 w-16 border-t border-dashed ${isDone ? 'border-primary-300' : 'border-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 text-sm">
          <span className="text-gray-500">Severity:</span>
          <span className="badge bg-gray-100 text-gray-700">{caseData.severity}</span>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 px-4 py-3 text-sm">
          <div className="flex gap-3"><dt className="text-gray-400 w-40">Current Processor</dt><dd className="text-gray-800">{caseData.assigned_user_name || '—'}</dd></div>
          <div className="flex gap-3"><dt className="text-gray-400 w-40">Response Duration</dt><dd className="text-gray-800">{duration(caseData.created_at, respondedAt)}</dd></div>
          <div className="flex gap-3"><dt className="text-gray-400 w-40">Reports sent</dt><dd className="text-gray-800">{reports.length}</dd></div>
          <div className="flex gap-3"><dt className="text-gray-400 w-40">Fixing Duration</dt><dd className="text-gray-800">{duration(caseData.created_at, fixedAt)}</dd></div>
        </dl>
      </div>

      <ol className="mt-5 space-y-3">
        {log.map((entry, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-1.5 w-2 h-2 rounded-full bg-primary-500 shrink-0" />
            <div className="flex-1 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
              <div className="text-sm font-medium text-gray-900">{entry.label}</div>
              <div className="text-xs text-gray-400 mb-1">{new Date(entry.at).toLocaleString('en-GB')}</div>
              <div className="text-sm text-gray-700">{entry.text}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
